'use client'

import { useState, useEffect } from 'react'
import { Button } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Textarea } from '@planner/ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@planner/ui'
import { useTranslation } from 'react-i18next'
import { useClientCalendar, type ReservationData } from '../hooks/useClientCalendar'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'

interface ReservationFormProps {
  selectedDate: Date | null
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function ReservationForm({ selectedDate, open, onClose, onSuccess }: ReservationFormProps) {
  const { t } = useTranslation()
  const { createReservation } = useClientCalendar(new Date(), 'dayGridMonth')
  const [formData, setFormData] = useState<ReservationData>({
    event_date: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
    number_of_guests: 1,
    title: '',
    description: '',
    full_name: '',
    email: '',
    phone: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Update form data when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        event_date: selectedDate.toISOString().split('T')[0]
      }))
    }
  }, [selectedDate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Validation
    if (!formData.event_date) {
      setError('Data wydarzenia jest wymagana')
      setLoading(false)
      return
    }

    if (!formData.title.trim()) {
      setError('Tytuł wydarzenia jest wymagany')
      setLoading(false)
      return
    }

    if (!formData.full_name.trim()) {
      setError('Imię i nazwisko są wymagane')
      setLoading(false)
      return
    }

    if (!formData.email.trim()) {
      setError('Adres e-mail jest wymagany')
      setLoading(false)
      return
    }

    if (!formData.phone.trim()) {
      setError('Numer telefonu jest wymagany')
      setLoading(false)
      return
    }

    if (formData.number_of_guests < 1) {
      setError('Liczba gości musi być większa niż 0')
      setLoading(false)
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError('Nieprawidłowy adres e-mail')
      setLoading(false)
      return
    }

    const result = await createReservation(formData)

    if (result.error) {
      setError(result.error)
      setLoading(false)
    } else {
      // Reset form
      setFormData({
        event_date: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
        number_of_guests: 1,
        title: '',
        description: '',
        full_name: '',
        email: '',
        phone: ''
      })
      setError(null)
      setLoading(false)
      onClose()
      if (onSuccess) {
        onSuccess()
      }
    }
  }

  const handleClose = () => {
    if (!loading) {
      setError(null)
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Wstępna rezerwacja</DialogTitle>
          <DialogDescription>
            {selectedDate && (
              <span>
                Rezerwacja na dzień: {format(selectedDate, 'd MMMM yyyy', { locale: pl })}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="event_date">
                Data wydarzenia *
              </Label>
              <Input
                id="event_date"
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                required
                disabled={!!selectedDate}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="title">
                Tytuł wydarzenia *
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Np. Wesele, Urodziny, Impreza firmowa"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="number_of_guests">
                Liczba gości *
              </Label>
              <Input
                id="number_of_guests"
                type="number"
                min="1"
                value={formData.number_of_guests}
                onChange={(e) => setFormData({ ...formData, number_of_guests: parseInt(e.target.value) || 1 })}
                required
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">
                Opis (opcjonalnie)
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Dodatkowe informacje o wydarzeniu..."
                rows={3}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="full_name">
                Imię i nazwisko *
              </Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                placeholder="Jan Kowalski"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Adres e-mail *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="jan.kowalski@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                Numer telefonu *
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                placeholder="+48 123 456 789"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Zapisywanie...' : 'Złóż rezerwację'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
